import React, { CSSProperties } from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';

import EllipsisWithTooltip from '../ellipsis-with-tooltip';

// typescript
import { Weights } from '../title';
import { EllipsisWithTooltipProps } from '../ellipsis-with-tooltip/ellipsis-with-tooltip';
import colors from '../../style/colors';
import { spacing } from '../../style';
import {
  CssUnitsAsResponsiveObject,
  getCssRuleWithResponsiveCalculation,
} from '../../utils/get-css-rule-with-responsive-calc';
import { sizes, variants } from './style';
import { ActionIcon } from './tag-action-icon';
import { TagContext } from './context';

export interface TagProps {
  id?: string;
  display?: 'flex';
  variant?: keyof typeof variants;
  'data-name'?: string;
  'data-value'?: string;
  'data-id'?: string;
  size?: keyof typeof sizes;
  disabled?: boolean;
  weight?: Weights;
  textTransform?: 'capitalize' | 'uppercase' | 'none';
  children?: React.ReactNode;
  className?: string;
  tabIndex?: number;
  whiteSpace?: 'pre-line' | 'break-spaces' | 'nowrap' | 'pre-wrap';
  maxWidth?: CssUnitsAsResponsiveObject<React.CSSProperties['maxWidth']>;
  componentClass?: 'button' | 'span';
  'aria-label'?: string;
  height?: 'auto';
  'data-testid'?: string;
  gap?: keyof typeof spacing;
  alignSelf?: CSSProperties['alignSelf'];
  block?: boolean;
}

const TagComponent = ({
  id,
  children,
  className,
  variant,
  'data-name': dataName,
  'data-value': dataValue,
  'data-id': dataId,
  tabIndex,
  componentClass: Component = 'span',
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  ...rest
}: TagProps) => (
  <Component
    id={id}
    tabIndex={tabIndex}
    className={classNames('box-tag', className, `variant-${variant}`)}
    data-name={dataName}
    data-value={dataValue}
    data-id={dataId}
    aria-label={ariaLabel}
    data-testid={dataTestId}
    {...rest}
  >
    {children}
  </Component>
);

const StyledTag = styled(TagComponent)<TagProps>`
  color: inherit;
  border: transparent;
  border-radius: 9999px;

  ${props => variants[props.variant || 'default']};
  ${props => props.size && sizes[props.size]};
  ${props =>
    props.block &&
    css`
      width: 100%;
      display: flex;
      justify-content: center;
    `}
  flex-shrink: 0;
  position: relative;
  line-height: 1.1875rem;
  display: ${props => props?.display || 'inline-flex'};
  align-self: ${props => props?.alignSelf};
  align-items: center;
  cursor: ${props => (props.disabled ? 'not-allowed' : props.componentClass === 'button' ? 'pointer' : 'default')};
  font-weight: ${props => props.weight || 400};
  text-transform: ${props => props.textTransform};
  gap: ${props => props.gap && spacing[props.gap]};
  ${props =>
    getCssRuleWithResponsiveCalculation<React.CSSProperties['maxWidth']>({
      property: 'max-width',
      value: props.maxWidth,
      formatValue: value => {
        if (typeof value === 'number') {
          return `${value}px`;
        }

        return value;
      },
    })};

  ${props =>
    props.whiteSpace &&
    css`
      white-space: ${props.whiteSpace};
    `};
  ${props =>
    props?.height &&
    css`
      height: ${props.height};
    `};

  &&& * {
    line-height: 1.25rem;
  }

  .tag-text {
    text-align: center;
  }
`;

const Tag = ({ size, ...props }: TagProps) => (
  <TagContext value={{ size }}>
    <StyledTag size={size || 'md'} {...props} />
  </TagContext>
);

Tag.ActionIcon = styled(ActionIcon)`
  &:focus-visible:not(.disabled):not([disabled]) {
    box-shadow: 0 0 0 3px ${colors.sky};
  }
`;

Tag.Text = ({ maxWidth = 240, ...rest }: EllipsisWithTooltipProps) => (
  <EllipsisWithTooltip className={'tag-text'} tooltipProps={{ block: true }} maxWidth={maxWidth} {...rest} />
);

export default Tag;
